import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { businessTypeLabel } from "@/constants/verticals";
import { Plus, Building2, Crown, ArrowRightLeft, TrendingUp, TrendingDown, Wallet, RefreshCw, Store, Link2, Unlink, FileSpreadsheet, FileText, Inbox, Check, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

export default function Branches() {
  const { companyId, setCompanyId, currentCompany, refreshCompanies, user } = useAuth();
  const isRoot = user?.role === "super_admin" && !!user?.is_root;
  const [data, setData] = useState({ group_id: null, branches: [] });
  const [consol, setConsol] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", phone: "" });
  const [range, setRange] = useState({ from_date: monthStart(), to_date: todayISO() });
  const [linkable, setLinkable] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkId, setLinkId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [b, c, lk] = await Promise.all([
        api.get("/branches"),
        api.get("/branches/consolidated", { params: range }),
        api.get("/branches/linkable").catch(() => ({ data: [] })),
      ]);
      setData(b.data);
      setConsol(c.data);
      setLinkable(lk.data || []);
      if (isRoot) {
        const rq = await api.get("/branch-requests", { params: { status: "pending" } }).then(r => r.data).catch(() => []);
        setRequests(rq || []);
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const reloadConsol = async () => {
    try { const c = await api.get("/branches/consolidated", { params: range }); setConsol(c.data); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const createBranch = async () => {
    if (!form.name.trim()) { toast.error("اسم الفرع مطلوب"); return; }
    setSaving(true);
    try {
      const { data: res } = await api.post("/branches", form);
      if (res?.pending) {
        toast.success(res.message || "تم إرسال طلب فتح الفرع للمدير العام للموافقة");
      } else {
        toast.success("تم فتح الفرع الجديد");
        await refreshCompanies();
      }
      setOpen(false); setForm({ name: "", address: "", phone: "" });
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const approveReq = async (id) => {
    try { await api.post(`/branch-requests/${id}/approve`); toast.success("تمت الموافقة وفتح الفرع"); await refreshCompanies(); await load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const rejectReq = async (id) => {
    const reason = window.prompt("سبب الرفض (اختياري)") || "";
    try { await api.post(`/branch-requests/${id}/reject`, null, { params: { reason } }); toast.success("تم رفض الطلب"); await load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const linkExisting = async () => {
    if (!linkId) { toast.error("اختر شركة"); return; }
    try {
      await api.post("/branches/link", { company_id: linkId });
      toast.success("تم ربط الشركة كفرع");
      setLinkOpen(false); setLinkId("");
      await refreshCompanies();
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const unlinkBranch = async (cid) => {
    if (!confirm("فك ربط هذا الفرع ليعود شركة مستقلة؟")) return;
    try {
      await api.post(`/branches/${cid}/unlink`);
      toast.success("تم فك الربط");
      await refreshCompanies();
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const switchTo = async (cid) => {
    setCompanyId(cid);
    toast.success("تم التبديل إلى الفرع");
  };

  const grand = consol?.grand || { sales: 0, purchases: 0, expenses: 0, profit: 0 };
  const gcur = consol?.base_currency || currentCompany?.base_currency || "SYP";

  const exportConsol = async (fmt) => {
    try {
      const res = await api.get(`/branches/consolidated/export?format=${fmt}`, { params: range, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `branches_consolidated.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6" dir="rtl" data-testid="branches-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <Store className="w-7 h-7 text-primary" /> فروع شركتي
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            كل فرع شركة مستقلة تمامًا — بمخزونه وعملائه وصناديقه — ويظهر في مبدّل الشركة أعلى الصفحة.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="open-link-branch"><Link2 className="w-4 h-4" /> ربط شركة قائمة</Button>
            </DialogTrigger>
            <DialogContent dir="rtl" data-testid="link-branch-dialog">
              <DialogHeader><DialogTitle>ربط شركة قائمة كفرع</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground bg-muted/50 rounded-md p-2 leading-relaxed">
                  اختر شركة قائمة لضمّها إلى مجموعة هذه الشركة كفرع. تبقى بياناتها كما هي وتظهر في التقرير الموحّد.
                </p>
                <Select value={linkId || "__none__"} onValueChange={v => setLinkId(v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="link-company-select"><SelectValue placeholder="اختر شركة" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— اختر —</SelectItem>
                    {linkable.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.base_currency}</SelectItem>)}
                  </SelectContent>
                </Select>
                {linkable.length === 0 && <div className="text-xs text-muted-foreground">لا توجد شركات مستقلة قابلة للربط.</div>}
              </div>
              <DialogFooter>
                <Button onClick={linkExisting} disabled={!linkId} data-testid="save-link-btn">ربط كفرع</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="open-add-branch"><Plus className="w-4 h-4" /> {isRoot ? "فتح فرع جديد" : "طلب فتح فرع"}</Button>
            </DialogTrigger>
            <DialogContent dir="rtl" data-testid="add-branch-dialog">
              <DialogHeader><DialogTitle>{isRoot ? "فتح فرع جديد" : "طلب فتح فرع جديد"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {!isRoot && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 leading-relaxed" data-testid="branch-approval-note">
                    سيُرسَل طلبك إلى المدير العام الرئيسي لاعتماده قبل فتح الفرع.
                  </p>
                )}
                <p className="text-xs text-muted-foreground bg-muted/50 rounded-md p-2 leading-relaxed">
                  يرث الفرع الجديد عملة ونوع نشاط واشتراك الشركة الحالية
                  {currentCompany ? ` (${businessTypeLabel(currentCompany.business_type)} · ${currentCompany.base_currency})` : ""}.
                </p>
                <div><Label>اسم الفرع</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="مثال: فرع دمشق - المزة" data-testid="branch-name-input" /></div>
                <div><Label>العنوان (اختياري)</Label>
                  <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid="branch-address-input" /></div>
                <div><Label>الهاتف (اختياري)</Label>
                  <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="branch-phone-input" /></div>
              </div>
              <DialogFooter>
                <Button onClick={createBranch} disabled={saving} data-testid="save-branch-btn">{saving ? "جارٍ..." : (isRoot ? "فتح الفرع" : "إرسال الطلب")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Branch-open requests — root approval */}
      {isRoot && requests.length > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50/50" data-testid="branch-requests-card">
          <div className="flex items-center gap-2 mb-3 font-heading font-bold text-amber-800">
            <Inbox className="w-5 h-5" /> طلبات فتح فروع بانتظار موافقتك ({requests.length})
          </div>
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 justify-between bg-card border border-border rounded-md p-3" data-testid={`branch-request-${r.id}`}>
                <div className="text-sm">
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-xs text-muted-foreground">
                    مقدّم الطلب: {r.requested_by_name || "—"} · الشركة الأم: {r.parent_company_name || "—"}
                    {r.address ? ` · ${r.address}` : ""}{r.phone ? ` · ${r.phone}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" className="gap-1.5 h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => approveReq(r.id)} data-testid={`approve-request-${r.id}`}><Check className="w-4 h-4" /> موافقة</Button>
                  <Button size="sm" variant="outline" className="gap-1.5 h-8 text-destructive" onClick={() => rejectReq(r.id)} data-testid={`reject-request-${r.id}`}><X className="w-4 h-4" /> رفض</Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Consolidated summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="consol-sales">
          <div className="flex items-center gap-2 text-emerald-600 text-xs font-semibold"><TrendingUp className="w-4 h-4" /> إجمالي المبيعات</div>
          <div className="text-xl font-bold mt-1 tabular-nums">{fmtMoney(grand.sales, gcur)}</div>
        </Card>
        <Card className="p-4" data-testid="consol-purchases">
          <div className="flex items-center gap-2 text-amber-600 text-xs font-semibold"><TrendingDown className="w-4 h-4" /> إجمالي المشتريات</div>
          <div className="text-xl font-bold mt-1 tabular-nums">{fmtMoney(grand.purchases, gcur)}</div>
        </Card>
        <Card className="p-4" data-testid="consol-expenses">
          <div className="flex items-center gap-2 text-rose-600 text-xs font-semibold"><Wallet className="w-4 h-4" /> إجمالي المصروفات</div>
          <div className="text-xl font-bold mt-1 tabular-nums">{fmtMoney(grand.expenses, gcur)}</div>
        </Card>
        <Card className="p-4 bg-primary/5 border-primary/20" data-testid="consol-profit">
          <div className="flex items-center gap-2 text-primary text-xs font-semibold"><TrendingUp className="w-4 h-4" /> صافي الربح (المجموعة)</div>
          <div className={`text-xl font-bold mt-1 tabular-nums ${grand.profit < 0 ? "text-destructive" : "text-primary"}`}>{fmtMoney(grand.profit, gcur)}</div>
        </Card>
      </div>

      {/* Date range for consolidated */}
      <Card className="p-3 flex flex-wrap items-end gap-3" data-testid="consol-filter">
        <div><Label className="text-xs">من تاريخ</Label>
          <Input type="date" value={range.from_date} onChange={e => setRange({ ...range, from_date: e.target.value })} className="h-9 w-40" data-testid="consol-from" /></div>
        <div><Label className="text-xs">إلى تاريخ</Label>
          <Input type="date" value={range.to_date} onChange={e => setRange({ ...range, to_date: e.target.value })} className="h-9 w-40" data-testid="consol-to" /></div>
        <Button variant="outline" className="gap-2 h-9" onClick={reloadConsol} data-testid="consol-apply"><RefreshCw className="w-4 h-4" /> تحديث</Button>
        <div className="flex items-center gap-2 ms-auto">
          <Button variant="outline" size="sm" className="gap-1.5 h-9" onClick={() => exportConsol("excel")} data-testid="consol-export-excel"><FileSpreadsheet className="w-4 h-4" /> Excel</Button>
          <Button variant="outline" size="sm" className="gap-1.5 h-9" onClick={() => exportConsol("pdf")} data-testid="consol-export-pdf"><FileText className="w-4 h-4" /> PDF</Button>
        </div>
        {consol?.mixed_base && (
          <span className="text-xs text-amber-600">⚠️ الفروع تستخدم عملات أساسية مختلفة — الإجمالي تقريبي.</span>
        )}
      </Card>

      {/* Comparison chart */}
      {(consol?.rows || []).length > 1 && (
        <Card className="p-4" data-testid="branch-chart">
          <div className="text-sm font-semibold mb-3">مقارنة الفروع — المبيعات والربح ({gcur})</div>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={(consol.rows || []).map(r => ({ name: r.name, "المبيعات": r.sales, "الربح": r.profit }))} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} width={70} />
                <Tooltip formatter={(v) => fmtMoney(v, gcur)} />
                <Legend />
                <Bar dataKey="المبيعات" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="الربح" fill="#4f46e5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Per-branch table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-start">الفرع</TableHead>
                <TableHead className="text-start">النشاط</TableHead>
                <TableHead className="text-center">المبيعات</TableHead>
                <TableHead className="text-center">المشتريات</TableHead>
                <TableHead className="text-center">المصروفات</TableHead>
                <TableHead className="text-center">الربح</TableHead>
                <TableHead className="text-center">إجراء</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (<TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">جارٍ التحميل...</TableCell></TableRow>)}
              {!loading && data.branches.map((b) => {
                const row = (consol?.rows || []).find(r => r.company_id === b.id) || {};
                return (
                  <TableRow key={b.id} data-testid={`branch-row-${b.id}`} className={b.is_current ? "bg-primary/5" : ""}>
                    <TableCell className="text-start font-semibold">
                      <div className="flex items-center gap-2">
                        {b.is_head ? <Crown className="w-4 h-4 text-amber-500" title="الشركة الرئيسية" /> : <Building2 className="w-4 h-4 text-muted-foreground" />}
                        <span>{b.name}</span>
                        {b.is_current && <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded">نشط الآن</span>}
                      </div>
                      {b.address && <div className="text-xs text-muted-foreground mt-0.5">{b.address}</div>}
                    </TableCell>
                    <TableCell className="text-start text-sm">{businessTypeLabel(b.business_type)} · {b.base_currency}</TableCell>
                    <TableCell className="text-center tabular-nums text-emerald-700">{fmtMoney(row.sales || 0, b.base_currency)}</TableCell>
                    <TableCell className="text-center tabular-nums text-amber-700">{fmtMoney(row.purchases || 0, b.base_currency)}</TableCell>
                    <TableCell className="text-center tabular-nums text-rose-700">{fmtMoney(row.expenses || 0, b.base_currency)}</TableCell>
                    <TableCell className={`text-center tabular-nums font-bold ${(row.profit || 0) < 0 ? "text-destructive" : "text-primary"}`}>{fmtMoney(row.profit || 0, b.base_currency)}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {!b.is_current && (
                          <Button size="sm" variant="outline" className="gap-1.5 h-8" onClick={() => switchTo(b.id)} data-testid={`switch-branch-${b.id}`}><ArrowRightLeft className="w-3.5 h-3.5" /> تبديل</Button>
                        )}
                        {!b.is_head && (
                          <Button size="sm" variant="ghost" className="gap-1.5 h-8 text-destructive" onClick={() => unlinkBranch(b.id)} data-testid={`unlink-branch-${b.id}`} title="فك الربط"><Unlink className="w-3.5 h-3.5" /></Button>
                        )}
                        {b.is_current && b.is_head && <span className="text-xs text-muted-foreground">—</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!loading && data.branches.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">لا توجد فروع بعد — اضغط «فتح فرع جديد».</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
